<template>
  <div id="app">
    <!-- 顶部导航栏 -->
    <div v-if="showNavigation" class="app-header">
      <div class="header-content">
        <div class="logo">
          <img src="/favicon.ico" alt="Logo" class="logo-img" />
          <span class="logo-text">太可智越TechB</span>
        </div>
        <el-menu
          :default-active="activeMenu"
          class="nav-menu"
          mode="horizontal"
          @select="handleMenuSelect"
          background-color="#fff"
          text-color="#606266"
          active-text-color="#409EFF"
        >
          <el-menu-item index="/home">
            <i class="el-icon-s-home"></i>
            <span class="menu-text">首页</span>
          </el-menu-item>
           <el-menu-item index="/word-basics">
            <i class="el-icon-edit-outline"></i>
            <span>单词基础</span>
          </el-menu-item>
          <el-menu-item index="/memory-curve">
            <i class="el-icon-alarm-clock"></i>
            <span>记忆曲线</span>
          </el-menu-item>
          <el-menu-item index="/self-test">
            <i class="el-icon-document"></i>
            <span>自主测试</span>
          </el-menu-item>
          <el-menu-item index="/learning-report">
            <i class="el-icon-data-analysis"></i>
            <span>学习报告</span>
          </el-menu-item>
          <el-menu-item index="/word-book">
            <i class="el-icon-collection"></i>
            <span>单词本</span>
          </el-menu-item>
          <el-menu-item index="/help">
            <i class="el-icon-question"></i>
            <span>帮助</span>
          </el-menu-item>
        </el-menu>
        <div class="header-actions">
          <el-dropdown @command="handleUserAction" trigger="click">
            <el-button type="text" class="user-btn">
              <i class="el-icon-user-solid"></i>
              {{ userName }}
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">
                <i class="el-icon-user"></i>个人资料
              </el-dropdown-item>
              <el-dropdown-item command="settings">
                <i class="el-icon-setting"></i>设置
              </el-dropdown-item>
              <el-dropdown-item command="logout" divided>
                <i class="el-icon-switch-button"></i>退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="app-main" :class="{ 'with-header': showNavigation }">
      <router-view />
    </div>

    <!-- 用户设置对话框 -->
    <UserSettingsDialog
      :visible.sync="settingsDialogVisible"
      @settings-saved="handleSettingsSaved"
    />

    <!-- 个人资料对话框 -->
    <UserProfileDialog
      :visible.sync="profileDialogVisible"
      @profile-updated="handleProfileUpdated"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import UserSettingsDialog from "@/components/UserSettingsDialog.vue";
import UserProfileDialog from "@/components/UserProfileDialog.vue";

export default {
  name: "App",
  components: {
    UserSettingsDialog,
    UserProfileDialog
  },
  data() {
    return {
      activeMenu: "/home",
      settingsDialogVisible: false,
      profileDialogVisible: false,
    };
  },
  computed: {
    ...mapGetters("user", ["userDisplayName"]),

    showNavigation() {
      // 登录页面不显示导航栏
      return this.$route.path !== "/login";
    },
    userName() {
      return this.userDisplayName || "用户";
    },
  },
  watch: {
    $route(to) {
      this.activeMenu = to.path;
    },
  },
  mounted() {
    this.activeMenu = this.$route.path;
  },
  methods: {
    ...mapActions("user", ["logout"]),

    /**
     * 处理菜单选择
     */
    handleMenuSelect(index) {
      if (index !== this.$route.path) {
        this.$router.push(index);
      }
    },

    /**
     * 处理用户操作
     */
    handleUserAction(command) {
      switch (command) {
        case "profile":
          this.profileDialogVisible = true;
          break;
        case "settings":
          this.settingsDialogVisible = true;
          break;
        case "logout":
          this.handleLogout();
          break;
      }
    },

    /**
     * 处理设置保存
     */
    handleSettingsSaved() {
      this.$message.success("设置已保存");
    },

    /**
     * 处理个人资料更新
     */
    handleProfileUpdated() {
      this.$message.success("个人资料更新成功");
    },

    /**
     * 退出登录
     */
    handleLogout() {
      this.$confirm("确定要退出登录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }).then(async () => {
        try {
          await this.logout();
          // 跳转到登录页
          this.$router.push("/login");
          this.$message.success("已退出登录");
        } catch (error) {
          console.error("退出登录错误:", error);
          this.$message.error("退出登录失败");
        }
      }).catch(() => {
        // 用户点击取消，不做任何操作
        console.log("用户取消退出登录");
      });
    },
  },
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  background-color: #f5f7fa;
}

/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
  line-height: 1.6;
  color: #333;
  background-color: #f5f7fa;
}

/* 顶部导航栏 */
.app-header {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 60px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 18px;
  color: #409eff;
  flex-shrink: 0;
  min-width: 140px;
}

.logo i {
  font-size: 24px;
}

.logo-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.logo-text {
  color: #303133;
}

.nav-menu {
  flex: 1;
  margin: 0 20px;
  border-bottom: none;
  max-width: 900px;
}

.nav-menu .el-menu-item {
  height: 60px;
  line-height: 60px;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  padding: 0 12px;
  font-size: 15px;
}

.nav-menu .el-menu-item:hover {
  background-color: #f5f7fa;
  color: #409eff;
}

.nav-menu .el-menu-item.is-active {
  border-bottom-color: #409eff;
  color: #409eff;
  background-color: transparent;
}

.nav-menu .el-menu-item i {
  margin-right: 6px;
  font-size: 16px;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 120px;
}

/* 更稳健的菜单布局：防止换行，当溢出时水平滚动（触控友好） */
.nav-menu .el-menu {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.nav-menu .el-menu-item {
  flex: 0 0 auto;
}

/* 当菜单项较多时，隐藏默认的横向滚动条视觉并保留交互 */
.nav-menu .el-menu::-webkit-scrollbar {
  height: 6px;
}
.nav-menu .el-menu::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,0.08);
  border-radius: 3px;
}

/* PC端宽屏优化 */
@media (min-width: 1200px) {
  .nav-menu {
    margin: 0 30px;
  }
  .nav-menu .el-menu-item {
    padding: 0 15px;
    font-size: 16px;
  }
}

/* 小屏/窄窗口时，减少 menu 左右 margin，避免与用户按钮重叠 */
@media (max-width: 1199px) {
  .nav-menu {
    margin: 0 12px;
  }
  .nav-menu .el-menu-item {
    padding: 0 10px;
    font-size: 14px;
  }
}

@media (max-width: 1100px) {
  .nav-menu {
    margin: 0 8px;
  }
  .nav-menu .el-menu-item {
    padding: 0 8px;
  }
}

.user-btn {
  padding: 8px 16px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.user-btn:hover {
  color: #409eff;
  background-color: #f5f7fa;
}

/* 主要内容区域 */
.app-main {
  min-height: 100vh;
}

.app-main.with-header {
  min-height: calc(100vh - 60px);
}

/* Element UI 样式覆盖 */
.el-button {
  border-radius: 6px;
}

.el-card {
  border-radius: 8px;
  border: none;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.el-input__inner {
  border-radius: 6px;
}

.el-table {
  border-radius: 8px;
  overflow: hidden;
}

.el-table th {
  background-color: #fafafa;
  font-weight: 600;
}

.el-dialog {
  border-radius: 8px;
}

.el-dropdown-menu {
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 平板通用样式：600px - 1024px */
@media (min-width: 600px) and (max-width: 1040px) {
  .header-content {
    padding: 0 10px;
    height: 56px;
    align-items: center;
  }
  .logo {
    font-size: 16px;
    gap: 6px;
  }
  .logo-img {
    width: 22px;
    height: 22px;
  }
  /* 默认保持菜单为水平布局，但限制溢出 */
  .nav-menu {
    margin: 0 8px;
    font-size: 14px;
    flex: 1 1 auto;
    display: flex !important;
    justify-content: center;
    overflow: hidden;
  }
  .nav-menu .el-menu-item {
    height: 48px;
    line-height: 48px;
    font-size: 14px;
    padding: 0 6px;
    white-space: nowrap;
  }
  .nav-menu .el-menu-item i {
    margin-right: 4px;
    font-size: 15px;
  }
  .header-actions {
    gap: 6px;
    flex-shrink: 0;
  }
  .user-btn {
    padding: 6px 10px;
    font-size: 15px;
  }
}

/* 1000px以下：菜单项太多，切换为仅图标模式 */
@media (max-width: 1000px) {
  .nav-menu .el-menu-item {
    flex: 1 1 0;
    display: flex !important;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 4px !important;
    min-width: 0;
    border-bottom: none;
  }
  .nav-menu .el-menu-item i {
    margin: 0 !important;
    font-size: 18px !important;
  }
  .nav-menu .el-menu-item span {
    display: none !important; /* 隐藏文字，仅保留图标 */
  }
}

/* 平板竖屏：简化为图标模式并均匀分布，提升触控体验 */
@media (min-width: 600px) and (max-width: 1024px) and (orientation: portrait) {
  .logo-text {
    display: none; /* 节省空间 */
  }
  .nav-menu {
    margin: 0;
    justify-content: space-between !important;
    width: 100%;
    padding: 0 6px;
  }
  
  /* 当空间有限时，让菜单可水平滚动（备选） */
  .nav-menu .el-menu {
    overflow-x: auto;
  }
}

/* 平板横屏：1000px以上显示图标+文字，1000px以下仅图标 */
@media (min-width: 1000px) and (max-width: 1024px) and (orientation: landscape) {
  .logo-text {
    display: inline-block;
  }
  .nav-menu {
    justify-content: center !important;
    margin: 0 12px;
  }
  .nav-menu .el-menu-item {
    padding: 0 8px !important;
    font-size: 14px !important;
    flex-direction: row !important;
  }
  .nav-menu .el-menu-item i {
    margin-right: 4px !important;
    font-size: 15px !important;
  }
  .nav-menu .el-menu-item span {
    display: inline !important;
  }
}

/* 手机小屏：小于600px 时仅显示图标 */
@media (max-width: 600px) {
  .logo-text {
    display: none;
  }

  .nav-menu {
    margin: 0;
    padding: 0 4px;
    justify-content: space-between !important;
  }

  .nav-menu .el-menu-item {
    width: auto;
    flex: 1 1 0;
    display: flex !important;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 3px !important;
    min-width: 0;
  }

  .nav-menu .el-menu-item i {
    font-size: 18px !important;
    margin: 0 !important;
  }

  .nav-menu .el-menu-item span {
    display: none !important;
  }

  .user-btn {
    padding: 4px 8px !important;
  }

  .user-btn i {
    margin-right: 0 !important;
    font-size: 18px !important;
  }

  .user-btn span {
    display: none !important;
  }
}
</style>
